import { red } from '@mui/material/colors';
import React, { useContext } from 'react';
import { get_cod } from '../../../utils/cod';
import {
  displayLocation,
  extractMapsPlaceLink,
} from '../../../utils/locations';
import { displayMoney } from '../../../utils/money';

import CopyLinkButton from '../../common/copy-link-button/copy-link-button';

import { GenericList } from '../../common/generic-list/generic-list';
import {
  IconWithText,
  mobileDoubleItemsGrid,
} from '../../common/styles/components';
import { showDeliveryBatchChip } from '../../common/styles/delivery-batch.styles';
import { showDeliveryState } from '../../common/styles/order-states.style';
import {
  getPickupTaskStateColor,
  getPickupTaskStateIcon,
  showPickupTaskChip,
  showPickupTaskState,
} from '../../common/styles/pickup-task.styles';
import { showWorkflow } from '../../common/styles/workflow-display';
import { Updater } from 'use-immer';
import { SortingByColumnState } from '../../common/generic-table/utils';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import StorefrontIcon from '@mui/icons-material/Storefront';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import InventoryIcon from '@mui/icons-material/Inventory';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import ChecklistIcon from '@mui/icons-material/Checklist';
import LinkIcon from '@mui/icons-material/Link';
import { IPickupTask } from '../../../interfaces/delivery-company/tasks';
import { format } from 'date-fns';
import { Box, CircularProgress, IconButton } from '@mui/material';
import { AuthContext } from '../../../contexts/user-context/user-context';
import HouseIcon from '@mui/icons-material/House';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import DownloadIcon from '@mui/icons-material/Download';
import { isDesktop, isMobile, isTablet } from 'react-device-detect';
import CancelIcon from '@mui/icons-material/Cancel';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import { DriverWithIcon } from '../../common/styles/driver-with-icon';
import { FactCheckOutlined } from '@mui/icons-material';
import ConfirmAction from '../../common/confirm-action/confirm-action';
import { ClearIcon } from '@mui/x-date-pickers';
import VerifiedIcon from '@mui/icons-material/Verified';
import EditIcon from '@mui/icons-material/Edit';
import PreviewIcon from '@mui/icons-material/Preview';
import RestorePageIcon from '@mui/icons-material/RestorePage';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PaymentIcon from '@mui/icons-material/Payment';
import NotesIcon from '@mui/icons-material/Notes';
import {
  IMerchantOrdersInvoice,
  MerchantOrdersInvoiceState,
} from '../../../interfaces/common/merchant-invoices';
import {
  canCancelMerchantInvoice,
  showMerchantInvoiceState,
} from '../../../utils/merchant-invoices';
import { MerchantWithIcon } from '../../common/styles/merchant-with-icon';
import {
  getInvoiceStateColor,
  getInvoiceStateIcon,
} from '../../common/styles/merchant-invoice';

interface ResponsiveMerchantInvoicesListingProps {
  invoicesList: IMerchantOrdersInvoice[];
  isLoading: boolean;
  setLockedInvoiceMeta: React.Dispatch<
    React.SetStateAction<IMerchantOrdersInvoice | null>
  >;
  setLockType: React.Dispatch<React.SetStateAction<'view' | 'delete' | null>>;
  fetchInvoiceContent: (invoicemeta: IMerchantOrdersInvoice) => Promise<void>;
  legacyFetchInvoiceContent: (invoicemeta: IMerchantOrdersInvoice) => Promise<void>;
  lockedInvoiceMeta: IMerchantOrdersInvoice | null;
  lockType: 'view' | 'delete' | null;
}

export const ResponsiveMerchantInvoicesListing = ({
  lockType,
  lockedInvoiceMeta,
  fetchInvoiceContent,
  legacyFetchInvoiceContent,
  invoicesList,
  isLoading,
  setLockedInvoiceMeta,
  setLockType,
}: ResponsiveMerchantInvoicesListingProps) => {
  const { user, config } = useContext(AuthContext);
  return (
    <GenericList<IMerchantOrdersInvoice>
      sourceView="merchant-invoices-listing"
      items={invoicesList}
      selectedItems={new Map()}
      updateSelectedItems={() => {
        throw new Error('unimplemented');
      }}
      isLoading={isLoading}
      fullWidth
      columns={[
        {
          id: 'id',
          header: <b>Invoice #</b>,
          display_body: (invoice) => {
            return <b>{invoice.id}</b>;
          },
        },
        {
          id: 'package_count',
          header: <b># Packages</b>,
          display_body: (invoice) => <p>{invoice.packages}</p>,
        },
        {
          id: 'due_amount',
          header: <b>Amount Due to Merchant</b>,
          display_body: (invoice) => (
            <>{displayMoney(invoice.net_due_to_merchant)}</>
          ),
        },
        {
          id: 'created_at',
          header: <b>Created At</b>,
          display_body: (invoice) => (
            <>{new Date(invoice.created_at).toLocaleString()}</>
          ),
        },
        {
          id: 'payment_state',
          header: <b>Payment State</b>,
          display_body: (invoice) => showMerchantInvoiceState(invoice.status),
        },
        {
          id: 'notes',
          header: <b>Notes</b>,
          display_body: (invoice) => (
            <div style={{ color: 'red' }}>
              {invoice.notes.reverse().join(' | ')}
            </div>
          ),
        },
        {
          id: 'view_invoice',
          header: <b>View</b>,
          display_body: (invoice) => (
            <>
              {' '}
              <IconButton
                onClick={() => legacyFetchInvoiceContent(invoice)}
                disabled={
                  lockedInvoiceMeta !== null ||
                  invoice.status == MerchantOrdersInvoiceState.CANCELLED
                }
              >
                {lockedInvoiceMeta?.id === invoice.id && lockType === 'view' ? (
                  <CircularProgress />
                ) : (
                  <PreviewIcon fontSize="large" />
                )}
              </IconButton>
            </>
          ),
        },

        {
          id: 'download_invoice',
          header: <b>View</b>,
          display_body: (invoice) => (
            <>
              {' '}
              <IconButton
                onClick={() => fetchInvoiceContent(invoice)}
                disabled={
                  lockedInvoiceMeta !== null ||
                  invoice.status == MerchantOrdersInvoiceState.CANCELLED
                }
              >
                {lockedInvoiceMeta?.id === invoice.id && lockType === 'view' ? (
                  <CircularProgress />
                ) : (
                  <DownloadIcon fontSize="large" />
                )}
              </IconButton>
            </>
          ),
        },
      ]}
      getItemUID={(order) => order.id}
      rowConfigs={[
        {
          display_row: (invoice) => <b>Invoice #{invoice.id}</b>,
        },
        {
          display_row: (invoice) => (
            <IconWithText centered>
              <Box
                key={'items'}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  backgroundColor: '#f0f0f0',
                  borderRadius: '12px',
                  padding: '4px 8px',
                }}
              >
                {invoice.packages} packages
              </Box>
            </IconWithText>
          ),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'amount'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>{'Net To Merchant'}</b>{' '}
                {displayMoney(invoice.net_due_to_merchant)}
              </div>,
              <div
                key={'date'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>Date</b>
                {new Date(invoice.created_at).toLocaleString()}
              </div>,
            ]),
        },
        {
          display_row: (invoice) =>
            mobileDoubleItemsGrid([
              <div
                key={'invoice notes'}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
              >
                <b>Notes</b>
                <>{invoice.notes.join(' | ')}</>
              </div>,
            ]),
        },
      ]}
      cardFooter={(invoice) => {
        return {
          content: (
            <IconWithText centered>
              {getInvoiceStateIcon(invoice.status)}
              <>{invoice.status.replaceAll('_', ' ')}</>
            </IconWithText>
          ),
          background: getInvoiceStateColor(invoice.status),
        };
      }}
      cardIcon={(invoice) => <MonetizationOnIcon color="primary" />}
      onCardClick={(invoice) => fetchInvoiceContent(invoice)}
    />
  );
};

export default ResponsiveMerchantInvoicesListing;
